import { FC, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Divider,
  Flex,
  HStack,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import { IconName } from '@shinetools/sunshine-icons';

import {
  FeatureName,
  TransactionPaymentMethod,
  TransactionStatus,
  TransactionType,
} from '__generated__/GQL';
import config from 'config';
import { formatAmount } from 'common/formatAmount';
import useCanCompanyUserAccessFeature from 'common/hooks/useCanCompanyUserAccessFeature';
import useCurrentLanguage from 'common/hooks/useCurrentLanguage';
import useFeatureFlagsSwitcher from 'common/hooks/useFeatureFlagsSwitcher';
import { formatPan } from 'common/paymentCards/utils';
import SunshineCard from 'components/_core/SunshineCard';
import SunshineIcon from 'components/_core/SunshineIcon';
import SquircleIcon from 'components/_core/SunshineSquircleIcon';
import SunshineTag from 'components/_core/SunshineTag';
import Text from 'components/_core/Text';
import { logEvent } from 'features/Analytics/analytics';
import { formatDate, P } from 'helpers/date';

import categoriesLocales, {
  LocalizedCategories,
} from '../../../../../common/categoriesLocales';
import paymentLocales from '../../locales';
import { useBankOverviewSearchParams } from '../hooks/useBankOverviewSearchParams';
import { useShouldMergeIntraCompanyFundsTransfers } from '../hooks/useShouldMergeIntraCompanyFundsTransfers';
import useTransactionInitiatorName from '../hooks/useTransactionInitiatorName';
import mapTransactionToRebrandedCategoryIconProps from '../mapTransactionToRebrandedCategoryIconProps';
import { getTransactionDescription } from '../utils';
import CancelTransferModal from './CancelTransferModal';
import Receipts from './components/Receipts';
import VatAmount from './components/VatAmount';
import Header from './Header';
import type { DetailedTransaction } from './hooks/useDetailedTransactionQuery';
import useToggleTransactionPersonalMutation from './hooks/useToggleTransactionPersonalMutation';
import locales from './locales';
import Note from './Note';
import OldIbanWarning from './OldIbanWarning';
import ShineCommissions from './ShineCommissions';
import SwiftTransferFeesTable from './SwiftTransferFeesTable';

const PAYMENT_METHODS_ELIGIBLE_TO_TRANSFER_CERTIFICATE = [
  TransactionPaymentMethod.InstantTransfer,
  TransactionPaymentMethod.Transfer,
];
const STATUSES_ELIGIBLE_TO_TRANSFER_CERTIFICATE = [
  TransactionStatus.Pending,
  TransactionStatus.Validated,
];

type MapTransactionToRebrandedPaymentIconParams = {
  transaction: Pick<DetailedTransaction, 'paymentMethod' | 'type'>;
  isMergedTransaction: boolean;
};

const mapTransactionToRebrandedPaymentIcon = ({
  isMergedTransaction,
  transaction,
}: MapTransactionToRebrandedPaymentIconParams): IconName => {
  const { paymentMethod, type } = transaction;

  if (isMergedTransaction) {
    return 'payment';
  }

  switch (paymentMethod) {
    case TransactionPaymentMethod.Card:
      return 'card-recto';
    case TransactionPaymentMethod.CashDeposit:
      return 'loan';
    case TransactionPaymentMethod.DirectDebit:
      return 'transfer';
    case TransactionPaymentMethod.Check:
      return 'check';
  }

  if (type === TransactionType.Payin) {
    return 'transaction-in';
  }

  return 'transaction-out';
};

const getStatusLabel = (status: string) => {
  switch (status) {
    case TransactionStatus.Canceled:
      return locales.STATUS_CANCELED;
    case TransactionStatus.Pending:
      return locales.STATUS_PENDING;
    case TransactionStatus.Validated:
    default:
      return null;
  }
};

interface BankSidebarProps {
  transaction: DetailedTransaction;
}

const formatTransactionDate = (transactionAt: number) => {
  const date = new Date(transactionAt);

  return locales.formatString(locales.transactionAt, {
    date: formatDate(date, 'PPP'),
    time: formatDate(date, 'p'),
  });
};

const BankSidebar: FC<BankSidebarProps> = ({ transaction }) => {
  const elementRef = useRef<HTMLDivElement>(null);
  const { featureFlags } = useFeatureFlagsSwitcher();
  const { shouldDisplayInitiatorName } =
    useTransactionInitiatorName(transaction);
  const cancelTransferModal = useDisclosure();
  const { canAccessFeature } = useCanCompanyUserAccessFeature();

  // For CANCELED transactions, we still want to display
  const iconProps = mapTransactionToRebrandedCategoryIconProps({
    transaction: {
      // a "regular" CategoryIcon (ie. not a disabled one)
      ...transaction,
      status: TransactionStatus.Validated,
    },
  });

  const {
    category,
    creditCard,
    currency,
    expectedReleaseDate,
    fee,
    isCancelable,
    isPersonal,
    note,
    paymentMethod,
    remainingAuthorized,
    status,
    swiftTransferPayin,
    transactionAt,
    transactionId,
    type,
  } = transaction;

  const { getTransactionHelpPath } = useBankOverviewSearchParams();

  const description = getTransactionDescription(transaction);
  const currentLanguage = useCurrentLanguage();
  const statusLabel = getStatusLabel(status);

  const [toggleTransactionPersonal] =
    useToggleTransactionPersonalMutation(transactionId);

  const shouldDisplayImprintInfo =
    remainingAuthorized && expectedReleaseDate && remainingAuthorized > 0;

  const shouldShowTransferCertificate =
    type === TransactionType.Payout &&
    PAYMENT_METHODS_ELIGIBLE_TO_TRANSFER_CERTIFICATE.includes(paymentMethod) &&
    STATUSES_ELIGIBLE_TO_TRANSFER_CERTIFICATE.includes(status);

  const onDownloadTransferCertificate = () => {
    if (status === TransactionStatus.Validated) {
      logEvent({
        name: 'Click_Bank_Transfer_Certificate_Download',
        properties: { lang: currentLanguage },
      });
      const transferCertificateUrl = `${config.shineApiHost}/export/bankTransferCertificate/${transactionId}/pdf?lang=${currentLanguage}`;
      window.open(transferCertificateUrl);
    }
  };

  const shouldDisplayTransactionActions =
    paymentMethod !== TransactionPaymentMethod.IntraCompanyFundsTransfer;

  const isMergeIntraCompanyFundsTransfer =
    useShouldMergeIntraCompanyFundsTransfers();
  const isMergedTransaction =
    isMergeIntraCompanyFundsTransfer &&
    paymentMethod === TransactionPaymentMethod.IntraCompanyFundsTransfer;

  const transactionDate = formatTransactionDate(transactionAt);

  return (
    <>
      <Header
        color={iconProps.color}
        isMergedTransaction={isMergedTransaction}
        ref={elementRef}
        transaction={transaction}
      />

      <Flex align="stretch" direction="column" marginBottom="space-8">
        <HStack justify="center" marginBottom="space-16" spacing="space-16">
          <SunshineTag
            colorScheme="grey"
            icon={mapTransactionToRebrandedPaymentIcon({
              isMergedTransaction,
              transaction,
            })}
          >
            {
              paymentLocales[
                paymentMethod as Exclude<
                  TransactionPaymentMethod,
                  TransactionPaymentMethod.SwiftTransfer
                >
              ]
            }
          </SunshineTag>

          {category !== 'DEFAULT' ? (
            <SunshineTag colorScheme={iconProps.color} icon={iconProps.name}>
              {category in categoriesLocales
                ? categoriesLocales[category as LocalizedCategories]
                : category}
            </SunshineTag>
          ) : null}
        </HStack>

        <OldIbanWarning transaction={transaction} />

        <Box padding="space-16">
          <VStack
            align="stretch"
            divider={<Divider />}
            marginBottom="space-24"
            marginTop="space-32"
            paddingX="space-8"
            spacing="space-16"
          >
            {statusLabel ? (
              <HStack justifyContent="space-between" spacing="space-16">
                <Text variant="secondary">{locales.STATUS}</Text>
                <Text variant="primary">{statusLabel}</Text>
              </HStack>
            ) : null}

            {shouldDisplayInitiatorName ? (
              <HStack justifyContent="space-between" spacing="space-16">
                <Text variant="secondary">{locales.fromInitiator}</Text>
                <Text variant="primary">{transaction.initiatorName}</Text>
              </HStack>
            ) : null}

            {creditCard &&
            creditCard.__typename !== 'CreditCardPendingOrder' ? (
              <HStack justifyContent="space-between" spacing="space-16">
                <Text variant="secondary">{locales.card}</Text>
                <Text variant="primary">{formatPan(creditCard.maskedPan)}</Text>
              </HStack>
            ) : null}

            <HStack
              alignItems="top"
              justifyContent="space-between"
              spacing="space-16"
            >
              <Text variant="secondary">{locales.paymentDate}</Text>
              <Text variant="primary">{transactionDate}</Text>
            </HStack>

            {shouldDisplayImprintInfo ? (
              <HStack
                alignItems="top"
                justifyContent="space-between"
                spacing="space-16"
              >
                <Text variant="secondary">{locales.remainingImprint}</Text>
                <Text variant="primary">
                  {formatAmount({
                    currency,
                    forcePlusSign: false,
                    value: remainingAuthorized,
                  })}
                </Text>
              </HStack>
            ) : null}

            {shouldDisplayImprintInfo ? (
              <HStack
                alignItems="top"
                justifyContent="space-between"
                spacing="space-16"
              >
                <Text variant="secondary">{locales.expectedReleaseOn}</Text>
                <Text variant="primary">
                  {formatDate(new Date(expectedReleaseDate), P)}
                </Text>
              </HStack>
            ) : null}

            {description ? (
              <HStack
                alignItems="top"
                justifyContent="space-between"
                spacing="space-16"
              >
                <Text variant="secondary">{locales.label}</Text>
                <Text variant="primary">
                  {getTransactionDescription(transaction)}
                </Text>
              </HStack>
            ) : null}

            {fee && !swiftTransferPayin ? (
              <ShineCommissions transaction={transaction} />
            ) : null}
          </VStack>
        </Box>

        <SwiftTransferFeesTable transaction={transaction} />

        {shouldDisplayTransactionActions ? (
          <>
            <Divider size="lg" />

            <Receipts transaction={transaction} />

            <Divider size="lg" />

            {/**
             * @todo Remove this section after multi doc release
             * @link https://linear.app/shine/issue/CAC-58
             */}
            {transaction.receipt &&
            !featureFlags.enableMultipleTransactionDocs ? (
              <>
                <VatAmount
                  border="none"
                  borderRadius="unset"
                  receipt={transaction.receipt}
                  transactionTotal={transaction.value}
                />
                <Divider size="lg" />
              </>
            ) : null}

            <VStack align="stretch" divider={<Divider />} spacing="0">
              {canAccessFeature({
                name: FeatureName.BankTransactions,
              }) ? (
                <SunshineCard
                  isBoxed={false}
                  isChecked={isPersonal}
                  onChange={() => toggleTransactionPersonal(!isPersonal)}
                  padding="space-24"
                  title={locales.markAsPersonalLabel}
                  variant="checkbox"
                >
                  <Text paddingTop="space-8" size="sm">
                    {locales.markAsPersonalDescription}{' '}
                    <SunshineIcon name="personal" />
                  </Text>
                </SunshineCard>
              ) : null}

              <Note
                key={transactionId}
                note={note ?? ''}
                transactionId={transactionId}
              />

              {shouldShowTransferCertificate ? (
                <>
                  <Divider size="lg" />

                  <SunshineCard
                    alignItems="center"
                    isBoxed={false}
                    isDisabled={status !== TransactionStatus.Validated}
                    onClick={onDownloadTransferCertificate}
                    padding="space-16"
                    title={
                      status === TransactionStatus.Validated
                        ? locales.downloadTransferCertificateDescriptionValidated
                        : locales.downloadTransferCertificateDescriptionPending
                    }
                  >
                    <SunshineCard.Slot name="aside">
                      <SunshineIcon color="light" name="download" />
                    </SunshineCard.Slot>
                  </SunshineCard>
                </>
              ) : null}

              {isCancelable ? (
                <SunshineCard
                  isBoxed={false}
                  onClick={cancelTransferModal.onOpen}
                  padding="space-16"
                >
                  <SunshineCard.Content>
                    <SunshineCard.Title>
                      {locales.cancelTransfer}
                    </SunshineCard.Title>
                  </SunshineCard.Content>
                  <SunshineCard.Slot name="action">
                    <SunshineCard.SunshineIcon name="chevron-right" />
                  </SunshineCard.Slot>
                </SunshineCard>
              ) : null}

              <>
                <Divider size="lg" />
                <SunshineCard
                  alignItems="center"
                  as={Link}
                  isBoxed={false}
                  padding="space-16"
                  to={getTransactionHelpPath(transactionId)}
                >
                  <SunshineCard.Slot name="aside">
                    <SquircleIcon name="information" />
                  </SunshineCard.Slot>

                  <SunshineCard.Content>
                    <SunshineCard.Title>{locales.needHelp}</SunshineCard.Title>
                    <SunshineCard.Text>{locales.knowMore}</SunshineCard.Text>
                  </SunshineCard.Content>

                  <SunshineCard.Slot name="action">
                    <SunshineCard.SunshineIcon name="chevron-right" />
                  </SunshineCard.Slot>
                </SunshineCard>
              </>
            </VStack>
          </>
        ) : null}
      </Flex>

      <CancelTransferModal
        isOpen={cancelTransferModal.isOpen}
        onClose={cancelTransferModal.onClose}
        transactionId={transactionId}
      />
    </>
  );
};

export default BankSidebar;
